import React, { ReactElement, useCallback, useContext, useEffect } from 'react';
import { isSome, Option } from 'fp-ts/Option';
import { ToasterContext, ToastType } from '@dexma/ui-components';

import i18n from 'asset/translation';
import { LayoutType } from 'domain/entity/layout';
import Dashboard from 'domain/entity/dashboard';
import { UpdateLayoutCriteriaBuilder } from 'use-case/layout/update/updateLayoutCriteria';
import { dispatchFormChangesView, useFormChangesView } from 'adapter/in/common/context/formChanges';
import { dispatchCurrentWidgetConfiguration } from 'adapter/in/common/context/currentWidgetConfiguration';
import { dispatchDashboardEdit, useDashboardEdit } from 'adapter/in/dashboards/context/EditDashboard';
import EditDashboardHeader from 'adapter/in/dashboards/pages/EditDashboard/Header';
import { dispatchWidgetMenuView, useWidgetMenuView, WIDGET_STATUS } from 'adapter/in/dashboards/context/EditDashboard/WidgetMenuView';
import { dispatchWidgetsView, useWidgetsView } from 'adapter/in/dashboards/context/Widgets/view';
import { useLayouts } from 'adapter/in/dashboards/context/Layouts';
import { useHeaderDiscardChangesCleanupActions } from 'adapter/in/dashboards/containers/Modals/DiscardChangesModal/useDiscardChangesCleanupActions';
import { useNavigation } from 'adapter/in/common/hooks/useNavigation';
import { dispatchAlertToaster, useAlertToaster } from 'adapter/in/common/context/Alert';
import { dispatchLayoutsView, useLayoutsView } from 'adapter/in/common/context/Layouts/LayoutsContext';

const EditDashboardHeaderContainer = (): ReactElement => {
    const { dashboard, showSidePanelMenu } = useDashboardEdit();
    const { editLayoutMode: isEditingLayout, hasLayoutChanges, layouts, selectedLayoutType } = useLayoutsView();
    const { setShowModalEditName, setModalDiscardChanges, setShowSidePanelMenu, reset: resetDashboardEdit } = dispatchDashboardEdit();
    const { setEditLayoutMode, reset: resetLayoutsView, setHasLayoutChanges, selectLayoutType } = dispatchLayoutsView();
    const { status } = useWidgetMenuView();
    const { hasChange: widgetHasChange } = useFormChangesView();
    const { show: showAlert, type: alertType } = useAlertToaster();
    const { resetAlert } = dispatchAlertToaster();
    const { resetWidgetMenu } = dispatchWidgetMenuView();
    const toastContext: { toast: (options: { text: string; type: ToastType }) => void } = useContext(ToasterContext);
    const { widgets } = useWidgetsView();
    const { toggleReloadWidgets } = dispatchWidgetsView();
    const {
        update: updateLayout,
        updateResult: updateLayoutResult,
        updateError: updateLayoutError,
        updateReset: updateLayoutReset,
        updateIsLoading: updateLayoutIsLoading,
        readResult: layoutsResult,
    } = useLayouts();

    const {
        exitEditModeCleanupAction,
        addWidgetCleanupAction,
        editLayoutCleanupAction,
        editLayoutChangeLayoutTypeCleanupAction,
        editLayoutSaveAndProceedAction,
    } = useHeaderDiscardChangesCleanupActions();

    const { navigationPush } = useNavigation();
    const nameDashboard = isSome(dashboard) ? dashboard.value.name : '';
    const hasLayoutElements = isSome(layoutsResult) && Object.values(layoutsResult.value).every((arr) => arr.widgets.length > 0);

    const determinePath = (maybeDashboard: Option<Dashboard>, hasLayoutElementsParam: boolean) =>
        isSome(maybeDashboard) && hasLayoutElementsParam ? maybeDashboard.value.id : '';

    const handleOnEditName = useCallback(() => setShowModalEditName(true), []);
    const { reset: resetCurrentWidgetConfiguration } = dispatchCurrentWidgetConfiguration();
    const { setHasChange } = dispatchFormChangesView();

    const handleExitEditMode = useCallback(() => {
        if (showAlert) {
            resetAlert();
        }
        if (widgetHasChange) {
            setModalDiscardChanges(true, exitEditModeCleanupAction);
        } else if (hasLayoutChanges) {
            setModalDiscardChanges(true, exitEditModeCleanupAction, editLayoutSaveAndProceedAction);
        } else if (!isEditingLayout) {
            resetDashboardEdit();
            resetLayoutsView();
            resetCurrentWidgetConfiguration();
            setHasChange(false);
            resetWidgetMenu();
            navigationPush(determinePath(dashboard, hasLayoutElements));
        } else {
            setEditLayoutMode(false);
            selectLayoutType(LayoutType.DESKTOP);
        }
    }, [widgetHasChange, hasLayoutChanges, widgets, isEditingLayout, showAlert, alertType, hasLayoutElements]);

    const exitEditModeEnabled = true;

    const resetEditWidget = () => {
        if (status === WIDGET_STATUS.EDITING && !widgetHasChange) {
            resetCurrentWidgetConfiguration();
            resetWidgetMenu();
        }
    };

    const handleOnAddWidget = useCallback(() => {
        if (widgetHasChange && status === WIDGET_STATUS.EDITING) {
            setModalDiscardChanges(true, addWidgetCleanupAction);
        } else if (hasLayoutChanges) {
            setModalDiscardChanges(true, addWidgetCleanupAction, editLayoutSaveAndProceedAction);
        } else {
            resetEditWidget();
            setEditLayoutMode(false);
            setShowSidePanelMenu(true);
            selectLayoutType(LayoutType.DESKTOP);
        }
    }, [hasLayoutChanges, widgetHasChange, status]);

    const addWidgetEnabled = !showSidePanelMenu || (showSidePanelMenu && status === WIDGET_STATUS.EDITING);

    const handleEditLayout = useCallback(() => {
        if (widgetHasChange) {
            setModalDiscardChanges(true, editLayoutCleanupAction);
        } else {
            setEditLayoutMode(true);
            setShowSidePanelMenu(false);
        }
        resetEditWidget();
    }, [widgetHasChange, status, alertType]);
    const editLayoutEnabled = hasLayoutElements;

    const addWidgetVariant = addWidgetEnabled && !isEditingLayout ? 'primary' : 'secondary';
    const editNameEnabled = addWidgetEnabled && !isEditingLayout;

    const handleSaveLayout = useCallback(() => {
        if (isSome(dashboard)) {
            updateLayout(
                UpdateLayoutCriteriaBuilder.create()
                    .withDashboardId(dashboard.value.id)
                    .withLayout(layouts.get(selectedLayoutType)!)
                    .build()
            );
            setHasLayoutChanges(false);
        }
    }, [layouts, selectedLayoutType]);

    const saveLayoutDisabled = !hasLayoutChanges;

    const handleSelectLayoutType = (layoutType: LayoutType) => {
        if (hasLayoutChanges) {
            setModalDiscardChanges(true, () => editLayoutChangeLayoutTypeCleanupAction(layoutType), editLayoutSaveAndProceedAction);
        } else {
            selectLayoutType(layoutType);
        }
    };

    useEffect(() => {
        const success = isSome(updateLayoutResult);
        if (success) {
            toastContext.toast({
                text: i18n.t('dashboards.edit.saveLayoutSuccess', {
                    replace: { layoutType: i18n.t(`dashboards.layouts.${selectedLayoutType.toLowerCase()}SaveFeedback`) },
                }),
                type: ToastType.SUCCESS,
            });
            toggleReloadWidgets(true);
        }

        const failure = isSome(updateLayoutError);
        if (failure) {
            toastContext.toast({
                text: i18n.t('dashboards.edit.saveLayoutFailure', {
                    replace: { layoutType: i18n.t(`dashboards.layouts.${selectedLayoutType.toLowerCase()}SaveFeedback`) },
                }),
                type: ToastType.ERROR,
            });
        }

        return () => {
            if (success || failure) {
                updateLayoutReset();
            }
        };
    }, [updateLayoutResult, updateLayoutError]);

    return (
        <EditDashboardHeader
            name={nameDashboard}
            onEditName={handleOnEditName}
            editNameEnabled={editNameEnabled}
            onExitEditMode={handleExitEditMode}
            exitEditModeEnabled={exitEditModeEnabled}
            onAddWidget={handleOnAddWidget}
            addWidgetEnabled={addWidgetEnabled}
            addWidgetVariant={addWidgetVariant}
            onEditLayout={handleEditLayout}
            editLayoutEnabled={editLayoutEnabled}
            isEditingLayout={isEditingLayout}
            saveLayoutDisabled={saveLayoutDisabled}
            saveLayoutLoading={updateLayoutIsLoading}
            onSaveLayout={handleSaveLayout}
            selectedLayout={selectedLayoutType}
            onSelectLayout={handleSelectLayoutType}
        />
    );
};

export default EditDashboardHeaderContainer;
